/**
 * The refusal vocabularies, rendered.
 *
 * `reason` on a computed date and on a rule gap is a closed vocabulary — `registration_date_required`,
 * `introduced_after_filing_date` — not prose. The server returns structured values and the display
 * text is composed here from those values plus the labels `GET /meta` serves.
 *
 * This is presentation, not derivation: nothing below decides *whether* a date was produced, only
 * how the server's answer reads. A code this file has never seen renders as itself rather than as
 * silence, because an unfamiliar refusal is still an answer and hiding it would be the one
 * outcome worse than showing a bare token.
 */

import { lowerLabel } from '~/lib/format';
import type { Vocabulary } from './describe';

/** The trigger whose date a `<trigger>_date_required` refusal is asking for. */
const REQUIRED_DATE: Readonly<Record<string, string>> = {
  priority_date_required: 'priority',
  publication_date_required: 'publication',
  registration_date_required: 'registration',
  grant_date_required: 'grant',
  lapse_date_required: 'lapse',
};

/** The base date the user supplies on the header form, where the refusal names one. */
export function missingDateFor(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return REQUIRED_DATE[reason] ?? null;
}

const ABSENCE: Readonly<Record<string, string>> = {
  introduced_after_filing_date: 'Introduced after this filing date',
  repealed_before_filing_date: 'Repealed before this filing date',
  register_closed: 'The register had closed',
  adopted_not_yet_commenced: 'Adopted, but no version had commenced',
  gap_in_chain: 'No version was in force on this date',
  no_versions_held: 'No version of this rule is held',
  no_term_spine: 'The register holds no rule constituting the right',
  rule_contradicted: 'The governing version is contradicted and withheld',
  spine_contradicted: 'The rule constituting the right is contradicted and withheld',
};

/**
 * One line saying why no date was produced. A refusal that names a date the engine was not given
 * says which one, using the trigger's own label rather than a spelling invented here.
 */
export function describeReason(
  reason: string | null | undefined,
  vocab: Vocabulary,
): string | null {
  if (!reason) return null;

  const trigger = REQUIRED_DATE[reason];
  if (trigger) return `Needs the ${lowerLabel(vocab.triggerLabel(trigger))} date`;

  return ABSENCE[reason] ?? reason;
}
